/**
 * Automated Winner Detection Engine
 * Real-time monitoring and detection of A/B test winners with configurable thresholds
 */

import { ABTest, TestMetrics } from '../types/template';
import { 
  compareVariants,
  ComparisonResult
} from '../utils/statisticalAnalysis';

export interface WinnerDetectionConfig {
  confidenceThreshold: number; // 95, 99
  minimumSampleSize: number;
  minimumTestDuration: number; // hours
  minimumImprovementThreshold: number; // percentage
  earlyStoppingEnabled: boolean;
  businessImpactThreshold?: number; // minimum revenue impact
  riskTolerance: 'conservative' | 'moderate' | 'aggressive';
}

export interface WinnerDetectionResult {
  hasWinner: boolean;
  winnerVariantId?: string;
  confidence: number;
  improvement: number;
  recommendation: 'implement' | 'continue' | 'stop_inconclusive';
  reasoning: string;
  statisticalAnalysis: ComparisonResult;
  businessImpact: {
    estimatedLift: number;
    revenueImpact?: number;
    riskAssessment: 'low' | 'medium' | 'high';
  };
  nextActions: string[];
  alerts: WinnerDetectionAlert[];
}

export interface WinnerDetectionAlert {
  id: string;
  type: 'winner_detected' | 'significance_reached' | 'sample_size_warning' | 'performance_decline' | 'early_stopping';
  severity: 'info' | 'warning' | 'critical';
  message: string;
  timestamp: Date;
  testId: string;
  data?: any;
}

export interface RealTimeMonitoringData {
  testId: string;
  timestamp: Date;
  variants: Array<{
    variantId: string;
    conversions: number;
    sampleSize: number;
    conversionRate: number;
  }>;
  cumulativeResults: ComparisonResult;
  alerts: WinnerDetectionAlert[];
}

export class WinnerDetectionEngine {
  private config: WinnerDetectionConfig;
  private monitoringData: Map<string, RealTimeMonitoringData[]> = new Map();
  private alertCallbacks: Array<(alert: WinnerDetectionAlert) => void> = [];

  constructor(config: WinnerDetectionConfig) {
    this.config = config;
  }

  async detectWinner(_test: ABTest): Promise<{ improvement: number; confidence: number }> {
    // Mock implementation for detecting a winner
    return {
      improvement: 28.7, // Example improvement percentage
      confidence: 96.5,  // Example confidence percentage
    };
  }
  
  /**
   * Analyze test performance and detect winners
   */
  public analyzeTest(test: ABTest): WinnerDetectionResult {
    const controlVariant = test.variants.find(v => v.isControl);
    const testVariants = test.variants.filter(v => !v.isControl);

    if (!controlVariant || testVariants.length === 0) {
      throw new Error('Invalid test configuration: missing control or test variants');
    }

    // For now, analyze against the first test variant
    const testVariant = testVariants[0];
    
    const controlMetrics = this.extractVariantMetrics(controlVariant, test.metrics);
    const variantMetrics = this.extractVariantMetrics(testVariant, test.metrics);

    // Perform statistical comparison
    const statisticalAnalysis = compareVariants(
      controlMetrics.conversions,
      controlMetrics.sampleSize,
      variantMetrics.conversions,
      variantMetrics.sampleSize
    );

    // Check winner detection criteria
    const winnerResult = this.evaluateWinnerCriteria(
      test,
      statisticalAnalysis,
      controlMetrics,
      variantMetrics
    );

    // Generate alerts
    const alerts = this.generateAlerts(test, winnerResult, statisticalAnalysis);

    // Store monitoring data
    this.updateMonitoringData(test.id, {
      testId: test.id,
      timestamp: new Date(),
      variants: [
        {
          variantId: controlVariant.id,
          conversions: controlMetrics.conversions,
          sampleSize: controlMetrics.sampleSize,
          conversionRate: controlMetrics.conversions / controlMetrics.sampleSize
        },
        {
          variantId: testVariant.id,
          conversions: variantMetrics.conversions,
          sampleSize: variantMetrics.sampleSize,
          conversionRate: variantMetrics.conversions / variantMetrics.sampleSize
        }
      ],
      cumulativeResults: statisticalAnalysis,
      alerts
    });

    return winnerResult;
  }

  /**
   * Real-time monitoring of test performance
   */
  public monitorTestRealTime(testId: string): RealTimeMonitoringData | null {
    const data = this.monitoringData.get(testId);
    return data && data.length > 0 ? data[data.length - 1] : null;
  }

  /**
   * Get historical monitoring data for a test
   */
  public getMonitoringHistory(testId: string): RealTimeMonitoringData[] {
    return this.monitoringData.get(testId) || [];
  }

  /**
   * Subscribe to winner detection alerts
   */
  public onAlert(callback: (alert: WinnerDetectionAlert) => void): void {
    this.alertCallbacks.push(callback);
  }

  /**
   * Update configuration
   */
  public updateConfig(config: Partial<WinnerDetectionConfig>): void {
    this.config = { ...this.config, ...config };
  }

  /**
   * Get current configuration
   */
  public getConfig(): WinnerDetectionConfig {
    return { ...this.config };
  }

  private extractVariantMetrics(variant: any, testMetrics?: TestMetrics) {
    // Extract metrics from test data or variant performance
    if (testMetrics?.primaryMetric) {
      const isControl = variant.isControl;
      return {
        conversions: isControl 
          ? Math.round(testMetrics.primaryMetric.control.value * testMetrics.primaryMetric.control.sampleSize / 100)
          : Math.round(testMetrics.primaryMetric.variants[0].value * testMetrics.primaryMetric.variants[0].sampleSize / 100),
        sampleSize: isControl 
          ? testMetrics.primaryMetric.control.sampleSize
          : testMetrics.primaryMetric.variants[0].sampleSize,
        conversionRate: isControl 
          ? testMetrics.primaryMetric.control.value / 100
          : testMetrics.primaryMetric.variants[0].value / 100
      };
    }

    // Fallback to variant performance data or generate demo data
    if (variant.performance) {
      return {
        conversions: Math.round(variant.performance.conversionRate * variant.performance.sampleSize / 100),
        sampleSize: variant.performance.sampleSize,
        conversionRate: variant.performance.conversionRate / 100
      };
    }

    // Generate demo data for testing
    const baseSampleSize = 1000 + Math.floor(Math.random() * 2000);
    const baseConversionRate = variant.isControl ? 0.035 : 0.042; // 3.5% vs 4.2%
    const conversions = Math.round(baseConversionRate * baseSampleSize);

    return {
      conversions,
      sampleSize: baseSampleSize,
      conversionRate: baseConversionRate
    };
  }

  private evaluateWinnerCriteria(
    test: ABTest,
    statisticalAnalysis: ComparisonResult,
    controlMetrics: any,
    variantMetrics: any
  ): WinnerDetectionResult {
    const testDurationHours = test.startedAt 
      ? (Date.now() - test.startedAt.getTime()) / (1000 * 60 * 60)
      : 0;

    const minSampleSize = Math.min(controlMetrics.sampleSize, variantMetrics.sampleSize);
    const improvement = statisticalAnalysis.improvement;
    const isSignificant = statisticalAnalysis.statistical.isSignificant;
    const confidence = Math.round((1 - statisticalAnalysis.statistical.pValue) * 100);

    // Determine if we have a winner based on configuration
    const meetsConfidenceThreshold = confidence >= this.config.confidenceThreshold;
    const meetsSampleSizeThreshold = minSampleSize >= this.config.minimumSampleSize;
    const meetsDurationThreshold = testDurationHours >= this.config.minimumTestDuration;
    const meetsImprovementThreshold = Math.abs(improvement) >= this.config.minimumImprovementThreshold;

    let hasWinner = false;
    let winnerVariantId: string | undefined;
    let recommendation: WinnerDetectionResult['recommendation'] = 'continue';
    let reasoning = '';

    // Check for winner first
    if (meetsConfidenceThreshold && meetsSampleSizeThreshold && meetsImprovementThreshold) {
      if (meetsDurationThreshold || this.config.earlyStoppingEnabled) {
        hasWinner = true;
        winnerVariantId = improvement > 0 ? 'variant' : 'control';
        recommendation = 'implement';
        reasoning = `Winner detected with ${confidence}% confidence and ${Math.abs(improvement).toFixed(1)}% improvement`;
      } else {
        reasoning = `Significant results detected but minimum test duration not met (${testDurationHours.toFixed(1)}h / ${this.config.minimumTestDuration}h required)`;
      }
    } else if (testDurationHours >= this.config.minimumTestDuration * 2 && !isSignificant) {
      // Only recommend stopping if test has run long enough AND is not significant
      recommendation = 'stop_inconclusive';
      reasoning = `Test has run for ${testDurationHours.toFixed(1)} hours without reaching significance`;
    } else {
      // Continue testing - identify missing criteria
      const missingCriteria = [];
      if (!meetsConfidenceThreshold) missingCriteria.push(`confidence (${confidence}% < ${this.config.confidenceThreshold}%)`);
      if (!meetsSampleSizeThreshold) missingCriteria.push(`sample size (${minSampleSize} < ${this.config.minimumSampleSize})`);
      if (!meetsImprovementThreshold) missingCriteria.push(`improvement (${Math.abs(improvement).toFixed(1)}% < ${this.config.minimumImprovementThreshold}%)`);
      if (!meetsDurationThreshold && !this.config.earlyStoppingEnabled) missingCriteria.push(`duration (${testDurationHours.toFixed(1)}h < ${this.config.minimumTestDuration}h)`);
      
      if (missingCriteria.length > 0) {
        reasoning = `Continue testing - missing criteria: ${missingCriteria.join(', ')}`;
      } else {
        reasoning = 'Continue monitoring test performance';
      }
    }

    // Calculate business impact
    const businessImpact = {
      estimatedLift: improvement,
      revenueImpact: statisticalAnalysis.businessImpact.revenueImpact,
      riskAssessment: this.assessRisk(statisticalAnalysis, minSampleSize) as 'low' | 'medium' | 'high'
    };

    // Generate next actions
    const nextActions = this.generateNextActions(recommendation, hasWinner, statisticalAnalysis);

    return {
      hasWinner,
      winnerVariantId,
      confidence,
      improvement,
      recommendation,
      reasoning,
      statisticalAnalysis,
      businessImpact,
      nextActions,
      alerts: []
    };
  }

  private assessRisk(statisticalAnalysis: ComparisonResult, sampleSize: number): string {
    const factors = [];
    
    if (statisticalAnalysis.statistical.pValue > 0.01) factors.push('moderate_significance');
    if (sampleSize < this.config.minimumSampleSize * 1.5) factors.push('limited_sample');
    if (Math.abs(statisticalAnalysis.improvement) < 10) factors.push('small_effect');
    if (statisticalAnalysis.statistical.powerAnalysis < 80) factors.push('low_power');

    if (factors.length === 0) return 'low';
    if (factors.length <= 2) return 'medium';
    return 'high';
  }

  private generateNextActions(
    recommendation: WinnerDetectionResult['recommendation'],
    hasWinner: boolean,
    statisticalAnalysis: ComparisonResult
  ): string[] {
    const actions = [];

    switch (recommendation) {
      case 'implement':
        actions.push('Implement winning variant');
        actions.push('Monitor post-implementation performance');
        actions.push('Document learnings and insights');
        if (hasWinner) {
          actions.push('Update template library with winning elements');
          actions.push('Plan follow-up optimization tests');
        }
        break;

      case 'continue':
        actions.push('Continue test monitoring');
        actions.push('Check progress daily');
        if (statisticalAnalysis.statistical.sampleSizeAdequacy === 'insufficient') {
          actions.push('Consider increasing traffic allocation');
        }
        actions.push('Prepare for winner implementation when criteria are met');
        break;

      case 'stop_inconclusive':
        actions.push('End test without implementing changes');
        actions.push('Analyze test learnings and insights');
        actions.push('Consider testing different elements or approaches');
        actions.push('Document why test was inconclusive');
        break;
    }

    return actions;
  }

  private generateAlerts(
    test: ABTest,
    winnerResult: WinnerDetectionResult,
    statisticalAnalysis: ComparisonResult
  ): WinnerDetectionAlert[] {
    const alerts: WinnerDetectionAlert[] = [];

    // Winner detected alert
    if (winnerResult.hasWinner) {
      alerts.push({
        id: `winner-${test.id}-${Date.now()}`,
        type: 'winner_detected',
        severity: 'critical',
        message: `Winner detected for test "${test.name}" with ${winnerResult.confidence}% confidence`,
        timestamp: new Date(),
        testId: test.id,
        data: {
          winnerVariantId: winnerResult.winnerVariantId,
          improvement: winnerResult.improvement,
          confidence: winnerResult.confidence
        }
      });
    }

    // Statistical significance alert
    if (statisticalAnalysis.statistical.isSignificant && !winnerResult.hasWinner) {
      alerts.push({
        id: `significance-${test.id}-${Date.now()}`,
        type: 'significance_reached',
        severity: 'warning',
        message: `Statistical significance reached but other criteria not met`,
        timestamp: new Date(),
        testId: test.id,
        data: {
          pValue: statisticalAnalysis.statistical.pValue,
          improvement: statisticalAnalysis.improvement
        }
      });
    }

    // Sample size warning
    if (statisticalAnalysis.statistical.sampleSizeAdequacy === 'insufficient') {
      alerts.push({
        id: `sample-size-${test.id}-${Date.now()}`,
        type: 'sample_size_warning',
        severity: 'warning',
        message: `Sample size below recommended minimum for reliable results`,
        timestamp: new Date(),
        testId: test.id,
        data: {
          adequacy: statisticalAnalysis.statistical.sampleSizeAdequacy
        }
      });
    }

    // Performance decline alert
    if (statisticalAnalysis.improvement < -10 && statisticalAnalysis.statistical.isSignificant) {
      alerts.push({
        id: `decline-${test.id}-${Date.now()}`,
        type: 'performance_decline',
        severity: 'critical',
        message: `Significant performance decline detected (${statisticalAnalysis.improvement.toFixed(1)}%)`,
        timestamp: new Date(),
        testId: test.id,
        data: {
          improvement: statisticalAnalysis.improvement,
          pValue: statisticalAnalysis.statistical.pValue
        }
      });
    }

    // Trigger alert callbacks
    alerts.forEach(alert => {
      this.alertCallbacks.forEach(callback => callback(alert));
    });

    return alerts;
  }

  private updateMonitoringData(testId: string, data: RealTimeMonitoringData): void {
    if (!this.monitoringData.has(testId)) {
      this.monitoringData.set(testId, []);
    }
    
    const history = this.monitoringData.get(testId)!;
    history.push(data);
    
    // Keep only last 100 data points to prevent memory issues
    if (history.length > 100) {
      history.splice(0, history.length - 100);
    }
  }
}

// Default configuration presets
export const WINNER_DETECTION_PRESETS: Record<string, WinnerDetectionConfig> = {
  conservative: {
    confidenceThreshold: 99,
    minimumSampleSize: 2000,
    minimumTestDuration: 168, // 7 days
    minimumImprovementThreshold: 15,
    earlyStoppingEnabled: false,
    riskTolerance: 'conservative'
  },
  moderate: {
    confidenceThreshold: 95,
    minimumSampleSize: 1000,
    minimumTestDuration: 72, // 3 days
    minimumImprovementThreshold: 10,
    earlyStoppingEnabled: true,
    riskTolerance: 'moderate'
  },
  aggressive: {
    confidenceThreshold: 90,
    minimumSampleSize: 500,
    minimumTestDuration: 24, // 1 day
    minimumImprovementThreshold: 5,
    earlyStoppingEnabled: true,
    riskTolerance: 'aggressive'
  }
};